import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

let turnstileScriptPromise;

function ensureTurnstileScriptLoaded() {
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', (e) => reject(e));
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });

  return turnstileScriptPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { siteKey, theme = 'dark', onToken, onExpire },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current != null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }));

  useEffect(() => {
    let isMounted = true;

    async function renderWidget() {
      const key = siteKey || process.env.REACT_APP_TURNSTILE_SITE_KEY;
      if (!key) {
        return;
      }

      await ensureTurnstileScriptLoaded();

      if (!isMounted) return;
      if (!window.turnstile) return;
      if (!containerRef.current) return;
      if (rendered) return;

      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: key,
        theme,
        callback: (token) => {
          onToken?.(token);
        },
        'expired-callback': () => {
          onExpire?.();
          onToken?.(null);
        },
        'error-callback': () => {
          onToken?.(null);
        }
      });

      widgetIdRef.current = widgetId;
      setRendered(true);
    }

    renderWidget();

    return () => {
      isMounted = false;
    };
  }, [onExpire, onToken, rendered, siteKey, theme]);

  return <div ref={containerRef} />;
});

export default TurnstileWidget;
